import { defineStore } from 'pinia';
import { doc, getDoc } from 'firebase/firestore';
import { normalizeDocumentTags } from '~/lib/workspaceDocuments';
import { runFreshRequest, type FreshRequestOptions } from '~/lib/requestFreshness';
import { stripUndefined } from '~/lib/stripUndefined';
import { toMillis } from '~/lib/timestamps';
import {
    createTenantDocument,
    deleteTenantDocument,
    fetchTenantCollection,
    updateTenantDocument,
    type TenantDocumentPayload,
} from '~/lib/tenantCollection';
import type { WorkspaceDocument } from '~/types/models';

type WorkspaceDocumentInput = Pick<WorkspaceDocument, 'title' | 'content' | 'status'> & Partial<Pick<WorkspaceDocument, 'tags' | 'projectId' | 'projectName' | 'clientId' | 'clientName' | 'folderId' | 'folderName'>>;

export const useWorkspaceDocumentsStore = defineStore('workspace-documents', {
    state: () => ({
        documents: [] as WorkspaceDocument[],
        loading: false,
        saving: false,
        error: null as string | null,
        loadedTenantId: null as string | null,
    }),

    actions: {
        _tenantId(): string {
            const tenantStore = useTenantStore();
            if (!tenantStore.currentTenantId) throw new Error('No tenant selected. Please log in.');
            return tenantStore.currentTenantId;
        },

        _actor(): { id: string; name: string } {
            const authStore = useAuthStore();
            const userStore = useUserStore();
            const id = authStore.user?.uid || userStore.user?.id || '';
            if (!id) throw new Error('You must be signed in to manage documentation.');
            return {
                id,
                name: authStore.user?.displayName || userStore.user?.displayName || userStore.user?.fullName || authStore.user?.email || 'Team member',
            };
        },

        _sort(documents: WorkspaceDocument[]): WorkspaceDocument[] {
            return [...documents].sort((a, b) => toMillis(b.updatedAt || b.createdAt) - toMillis(a.updatedAt || a.createdAt));
        },

        async fetchAll(options: FreshRequestOptions = {}): Promise<void> {
            const { $db } = useNuxtApp();
            const tenantId = this._tenantId();
            this.loading = true;
            this.error = null;
            if (this.loadedTenantId !== tenantId) this.documents = [];
            try {
                await runFreshRequest(`workspace-documents:${tenantId}:all`, async () => {
                    this.documents = this._sort(await fetchTenantCollection<WorkspaceDocument>($db, tenantId, 'workspaceDocuments'));
                    this.loadedTenantId = tenantId;
                }, {
                    ...options,
                    hasData: this.loadedTenantId === tenantId,
                });
            } catch (error: any) {
                this.error = error?.message || 'Unable to load documentation.';
                throw error;
            } finally {
                this.loading = false;
            }
        },

        async fetchById(documentId: string): Promise<WorkspaceDocument | null> {
            const cached = this.documents.find(document => document.id === documentId);
            if (cached) return cached;
            const { $db } = useNuxtApp();
            const tenantId = this._tenantId();
            const snapshot = await getDoc(doc($db, `tenants/${tenantId}/workspaceDocuments/${documentId}`));
            if (!snapshot.exists()) return null;
            const document = { id: snapshot.id, ...snapshot.data() } as WorkspaceDocument;
            this.documents = this._sort([document, ...this.documents]);
            return document;
        },

        async createDocument(input: WorkspaceDocumentInput): Promise<string> {
            const { $db } = useNuxtApp();
            const tenantId = this._tenantId();
            const actor = this._actor();
            const now = new Date();
            const payload = stripUndefined({
                tenantId,
                ...input,
                title: String(input.title || '').trim() || 'Untitled document',
                content: String(input.content || ''),
                tags: normalizeDocumentTags(input.tags || []),
                createdBy: actor.id,
                createdByName: actor.name,
                updatedBy: actor.id,
                updatedByName: actor.name,
                publishedAt: input.status === 'published' ? now : undefined,
                createdAt: now,
                updatedAt: now,
            });
            this.saving = true;
            this.error = null;
            try {
                const id = await createTenantDocument($db, tenantId, 'workspaceDocuments', payload as TenantDocumentPayload);
                this.documents = this._sort([{ id, ...payload } as WorkspaceDocument, ...this.documents]);
                return id;
            } catch (error: any) {
                this.error = error?.message || 'Unable to create the document.';
                throw error;
            } finally {
                this.saving = false;
            }
        },

        async updateDocument(documentId: string, patch: Partial<WorkspaceDocument>): Promise<void> {
            const { $db } = useNuxtApp();
            const tenantId = this._tenantId();
            const actor = this._actor();
            const existing = this.documents.find(document => document.id === documentId);
            const nextStatus = patch.status || existing?.status;
            const payload = stripUndefined({
                ...patch,
                id: undefined,
                tenantId: undefined,
                title: patch.title === undefined ? undefined : String(patch.title || '').trim() || 'Untitled document',
                tags: patch.tags === undefined ? undefined : normalizeDocumentTags(patch.tags),
                updatedBy: actor.id,
                updatedByName: actor.name,
                publishedAt: nextStatus === 'published' && existing?.status !== 'published' ? new Date() : undefined,
                updatedAt: new Date(),
            });
            this.saving = true;
            this.error = null;
            try {
                await updateTenantDocument($db, tenantId, 'workspaceDocuments', documentId, payload as TenantDocumentPayload);
                this.documents = this._sort(this.documents.map(document => document.id === documentId ? { ...document, ...payload } as WorkspaceDocument : document));
            } catch (error: any) {
                this.error = error?.message || 'Unable to update the document.';
                throw error;
            } finally {
                this.saving = false;
            }
        },

        async deleteDocument(documentId: string): Promise<void> {
            const { $db } = useNuxtApp();
            const tenantId = this._tenantId();
            this.saving = true;
            this.error = null;
            try {
                await deleteTenantDocument($db, tenantId, 'workspaceDocuments', documentId);
                this.documents = this.documents.filter(document => document.id !== documentId);
            } catch (error: any) {
                this.error = error?.message || 'Unable to delete the document.';
                throw error;
            } finally {
                this.saving = false;
            }
        },
    },
});
